import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, MessageSquare, Trash2, UserCheck, ArrowLeft, TrendingUp, Users, UserCheck as LeadIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function InsightStat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-brandborder rounded-md px-4 py-3 flex items-center gap-3">
      <Icon size={18} className="text-brandblue shrink-0" />
      <div>
        <div className="font-heading font-extrabold text-2xl text-navy leading-none">{value}</div>
        <div className="text-xs text-slate mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function AdminChats() {
  const { user } = useAuth();
  const canEdit = user?.role !== "read_only";

  const [sessions, setSessions] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, i] = await Promise.all([
        api.get("/chat/sessions"),
        api.get("/chat/insights"),
      ]);
      setSessions(s.data);
      setInsights(i.data);
    } catch {
      toast.error("Failed to load conversations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openSession = async (sid) => {
    setActive(sid);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/chat/sessions/${sid}`);
      setDetail(data);
    } catch {
      toast.error("Failed to load conversation.");
    } finally {
      setDetailLoading(false);
    }
  };

  const del = async (sid, e) => {
    e?.stopPropagation();
    if (!canEdit) return;
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(`/chat/sessions/${sid}`);
      setSessions((s) => s.filter((x) => x.session_id !== sid));
      if (active === sid) { setActive(null); setDetail(null); }
      toast.success("Conversation deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="page-admin-chats">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-navy">Conversations</h1>
          <p className="text-slate text-sm mt-1">What visitors are asking Ray, newest first.</p>
        </div>
        <button onClick={load} data-testid="chats-refresh-btn" className="inline-flex items-center gap-2 border border-brandborder text-navy font-semibold text-sm px-4 py-2.5 rounded-sm hover:border-brandblue transition-colors">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {insights && (
        <div className="mb-8 grid gap-6 lg:grid-cols-[1fr_1.3fr]" data-testid="chats-insights">
          <div className="grid grid-cols-3 gap-3">
            <InsightStat label="Conversations" value={insights.total_sessions} icon={Users} />
            <InsightStat label="Messages" value={insights.total_messages} icon={MessageSquare} />
            <InsightStat label="Leads from chat" value={insights.leads_from_chat} icon={LeadIcon} />
          </div>
          <div className="bg-white border border-brandborder rounded-md p-5" data-testid="popular-questions">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={16} className="text-brandblue" />
              <h2 className="text-sm font-semibold text-navy uppercase tracking-wide">Popular questions</h2>
            </div>
            {insights.top_questions.length === 0 ? (
              <p className="text-sm text-slate">No questions asked yet.</p>
            ) : (
              <ol className="space-y-2">
                {insights.top_questions.map((q, i) => (
                  <li key={i} className="flex items-start justify-between gap-3 text-sm" data-testid={`popular-question-${i}`}>
                    <span className="text-navy truncate">{q.question}</span>
                    <span className="shrink-0 text-xs bg-iceblue text-brandblue font-semibold px-2 py-0.5 rounded-sm">{q.count}×</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        {/* List */}
        <div className={`bg-white border border-brandborder rounded-md overflow-hidden ${active ? "hidden lg:block" : ""}`}>
          {loading ? (
            <div className="p-10 text-center text-slate">Loading…</div>
          ) : sessions.length === 0 ? (
            <div className="p-10 text-center text-slate" data-testid="chats-empty">No conversations yet.</div>
          ) : (
            <ul className="divide-y divide-brandborder" data-testid="chats-list">
              {sessions.map((s) => (
                <li key={s.session_id}>
                  <button
                    onClick={() => openSession(s.session_id)}
                    data-testid={`chat-session-${s.session_id}`}
                    className={`group w-full text-left px-5 py-4 flex gap-3 transition-colors hover:bg-lightbg ${active === s.session_id ? "bg-lightbg" : ""}`}
                  >
                    <MessageSquare size={18} className="mt-0.5 shrink-0 text-brandblue" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-navy font-medium truncate">{s.preview || "Conversation"}</p>
                      <div className="mt-1 flex items-center gap-2 text-xs text-slate">
                        <span>{s.message_count} msgs</span>
                        <span>·</span>
                        <span>{new Date(s.last_at).toLocaleString()}</span>
                      </div>
                      {s.lead && (
                        <span className="mt-1.5 inline-flex items-center gap-1 text-xs bg-iceblue text-brandblue font-semibold px-2 py-0.5 rounded-sm" data-testid="chat-session-lead-badge">
                          <UserCheck size={12} /> Lead: {s.lead.name}
                        </span>
                      )}
                    </div>
                    {canEdit && (
                      <Trash2 size={15} onClick={(e) => del(s.session_id, e)} className="mt-0.5 shrink-0 text-slate opacity-0 group-hover:opacity-100 hover:text-destructive transition" data-testid={`chat-session-delete-${s.session_id}`} />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="bg-white border border-brandborder rounded-md min-h-[400px] flex flex-col">
          {!active ? (
            <div className="flex-1 grid place-items-center text-slate text-sm p-10" data-testid="chats-detail-empty">
              Select a conversation to read it.
            </div>
          ) : detailLoading ? (
            <div className="flex-1 grid place-items-center text-slate p-10">Loading…</div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-brandborder px-5 py-4">
                <button onClick={() => { setActive(null); setDetail(null); }} className="lg:hidden text-slate hover:text-brandblue" data-testid="chats-back">
                  <ArrowLeft size={18} />
                </button>
                <div className="min-w-0">
                  {detail?.lead ? (
                    <div className="text-sm">
                      <span className="font-semibold text-navy">{detail.lead.name}</span>
                      <a href={`mailto:${detail.lead.email}`} className="ml-2 text-brandblue">{detail.lead.email}</a>
                      <span className="ml-2 text-xs bg-iceblue text-brandblue font-semibold px-2 py-0.5 rounded-sm">Lead captured</span>
                    </div>
                  ) : (
                    <span className="text-sm font-semibold text-navy">Anonymous visitor</span>
                  )}
                </div>
              </div>
              <div className="flex-1 space-y-4 overflow-y-auto p-5 bg-lightbg" data-testid="chats-transcript">
                {detail?.messages?.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "rounded-br-sm bg-brandblue text-white" : "rounded-bl-sm bg-white text-navy shadow-sm"}`}>
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
